import dayjs from "dayjs";
import * as DATE_PERIOD from "constants/dateFilter";
import {
  MONTH_INDEX,
  TAX_YEAR_END,
  TAX_YEAR_END_TYPES,
} from "constants/bookkeeping";

type DateRange = {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
};

export type DateRangeValue = (typeof DATE_PERIOD)[keyof typeof DATE_PERIOD];

export const getDateRange = (
  value: DateRangeValue,
  taxYearEnd?: TAX_YEAR_END_TYPES
): DateRange => {
  switch (value) {
    case DATE_PERIOD.CURRENT_WEEK: {
      return {
        startDate: dayjs().startOf("week"),
        endDate: dayjs().endOf("week"),
      };
    }
    case DATE_PERIOD.NEXT_WEEK: {
      return {
        startDate: dayjs().add(1, "week").startOf("week"),
        endDate: dayjs().add(1, "week").endOf("week"),
      };
    }
    case DATE_PERIOD.LAST_MONTH: {
      return {
        startDate: dayjs().subtract(1, "month").startOf("month"),
        endDate: dayjs().subtract(1, "month").endOf("month"),
      };
    }
    case DATE_PERIOD.CURRENT_MONTH: {
      return {
        startDate: dayjs().startOf("month"),
        endDate: dayjs().endOf("month"),
      };
    }
    case DATE_PERIOD.NEXT_MONTH: {
      return {
        startDate: dayjs().add(1, "month").startOf("month"),
        endDate: dayjs().add(1, "month").endOf("month"),
      };
    }
    case DATE_PERIOD.CURRENT_QUARTER: {
      return {
        startDate: dayjs().startOf("quarter"),
        endDate: dayjs().endOf("quarter"),
      };
    }
    case DATE_PERIOD.LAST_QUARTER: {
      return {
        startDate: dayjs().subtract(1, "quarter").startOf("quarter"),
        endDate: dayjs().subtract(1, "quarter").endOf("quarter"),
      };
    }
    case DATE_PERIOD.NEXT_QUARTER: {
      return {
        startDate: dayjs().add(1, "quarter").startOf("quarter"),
        endDate: dayjs().add(1, "quarter").endOf("quarter"),
      };
    }
    case DATE_PERIOD.LAST_3_MONTHS: {
      return {
        startDate: dayjs().subtract(3, "month").startOf("month"),
        endDate: dayjs().subtract(1, "month").endOf("month"),
      };
    }
    case DATE_PERIOD.LAST_6_MONTHS: {
      return {
        startDate: dayjs().subtract(6, "month").startOf("month"),
        endDate: dayjs().subtract(1, "month").endOf("month"),
      };
    }
    case DATE_PERIOD.LAST_12_MONTHS: {
      return {
        startDate: dayjs().subtract(12, "month").startOf("month"),
        endDate: dayjs().subtract(1, "month").endOf("month"),
      };
    }
    case DATE_PERIOD.CURRENT_TAX_YEAR: {
      if (!taxYearEnd || taxYearEnd === TAX_YEAR_END.JAN_DEC) {
        return {
          startDate: dayjs().startOf("year"),
          endDate: dayjs(),
        };
      }

      const [startAbbr] = taxYearEnd.split("_");
      const startMonthIndex = MONTH_INDEX[startAbbr];
      const currentMonth = dayjs().month();
      const fiscalYearStart =
        currentMonth >= startMonthIndex
          ? dayjs().month(startMonthIndex).startOf("month")
          : dayjs().subtract(1, "year").month(startMonthIndex).startOf("month");

      return {
        startDate: fiscalYearStart,
        endDate: dayjs(),
      };
    }
    case DATE_PERIOD.LAST_TAX_YEAR: {
      if (!taxYearEnd || taxYearEnd === TAX_YEAR_END.JAN_DEC) {
        return {
          startDate: dayjs().subtract(1, "year").startOf("year"),
          endDate: dayjs().subtract(1, "year").endOf("year"),
        };
      }

      const [startAbbr, endAbbr] = taxYearEnd.split("_");
      const startMonthIndex = MONTH_INDEX[startAbbr];
      const endMonthIndex = MONTH_INDEX[endAbbr];
      const currentMonth = dayjs().month();

      const lastFiscalYearStart =
        currentMonth >= startMonthIndex
          ? dayjs().subtract(1, "year").month(startMonthIndex).startOf("month")
          : dayjs().subtract(2, "year").month(startMonthIndex).startOf("month");

      const lastFiscalYearEnd =
        currentMonth >= startMonthIndex
          ? dayjs().month(endMonthIndex).endOf("month")
          : dayjs().subtract(1, "year").month(endMonthIndex).endOf("month");

      return {
        startDate: lastFiscalYearStart,
        endDate: lastFiscalYearEnd,
      };
    }

    case DATE_PERIOD.CURRENT_YEAR: {
      return {
        startDate: dayjs().startOf("year"),
        endDate: dayjs(),
      };
    }
    case DATE_PERIOD.LAST_YEAR: {
      return {
        startDate: dayjs().subtract(1, "year").startOf("year"),
        endDate: dayjs().subtract(1, "year").endOf("year"),
      };
    }
    case DATE_PERIOD.LAST_30_DAYS: {
      return {
        startDate: dayjs().subtract(30, "days"),
        endDate: dayjs(),
      };
    }
    case DATE_PERIOD.LAST_90_DAYS: {
      return {
        startDate: dayjs().subtract(90, "days"),
        endDate: dayjs(),
      };
    }
    case DATE_PERIOD.LAST_180_DAYS: {
      return {
        startDate: dayjs().subtract(180, "days"),
        endDate: dayjs(),
      };
    }
  }

  return {
    startDate: dayjs(),
    endDate: dayjs(),
  };
};

export const getDateRangeWithSetting = (
  value: DateRangeValue,
  taxYearEnd?: TAX_YEAR_END_TYPES,
  bookkeepingStartDate?: string
): DateRange => {
  const range = getDateRange(value, taxYearEnd);

  if (!bookkeepingStartDate) return range;

  const bkStartDate = dayjs(bookkeepingStartDate);

  if (range.startDate.isBefore(bkStartDate, "day")) {
    return { ...range, startDate: bkStartDate };
  }

  return range;
};
